import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Badge, Button } from 'theme-ui';
import { Modal, ModalBody, ModalHeader, ModalFooter } from "reactstrap";
import { ColumnDef } from '@tanstack/react-table';
import { FaInfoCircle } from 'react-icons/fa';
import DataTable from "../Tabels/Tabel";
import { SheetIndexItem, SheetIndexModalProps } from "./DeliveryHistory.types";
import { getTabStyle, normalizeTradeName } from "../../utils/GenerateQueryString";
import { formatModalHeading } from "./deliveryHistory.utils";

const SheetIndexModal: React.FC<SheetIndexModalProps> = ({
  isOpen,
  onClose,
  stageName,
  stageColor,
  sheetIndex,
  trades: tradesList,
  isDark,
  closeButtonText,
}) => {
  const { createprojectMasterData } = useSelector((state: Record<string, unknown>) => (state.project as { createprojectMasterData?: { allTask?: { data?: Array<Record<string, unknown>> } } }) || {});
  const masterTasks = useMemo(() => createprojectMasterData?.allTask?.data || [], [createprojectMasterData]);
  const [activeTab, setActiveTab] = useState<string>('All');

  useEffect(() => {
    if (isOpen) {
      setActiveTab('All');
    }
  }, [isOpen]);

  const modalBg = isDark ? '#20283c' : '#ffffff';
  const headerBg = isDark ? '#1a2135' : '#f8fafc';
  const borderColor = isDark ? '#334758' : '#e2e8f0';
  const titleColor = isDark ? '#ffffff' : '#0f172a';
  const accentColor = isDark ? '#BB86FC' : '#7743DB';

  // Unique trade tabs derived from sheetIndex
  const tradeTabs = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];

    const addTrade = (raw: string) => {
      const normalized = normalizeTradeName(raw);
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        list.push(normalized);
      }
    };

    (sheetIndex || []).forEach((item) => addTrade(item.discipline || ''));

    return ['All', ...list];
  }, [sheetIndex]);

  const filteredData = useMemo(() => {
    if (activeTab === 'All') return sheetIndex || [];
    return (sheetIndex || []).filter(item => {
      const normalized = normalizeTradeName(item.discipline || '');
      return normalized.toLowerCase() === activeTab.toLowerCase();
    });
  }, [sheetIndex, activeTab]);

  const columns = useMemo<ColumnDef<SheetIndexItem>[]>(() => [
    {
      header: "Sheet No.",
      accessorKey: "sheetNo",
      cell: (info) => {
        const item = info.row.original as SheetIndexItem & { status?: string };
        const rawStatus = (item.status || item.notes || '').trim().toLowerCase();
        const isUpdated = rawStatus === 'updated' || Boolean(item.isUpdated);
        return (
          <Box sx={{
            fontWeight: isUpdated ? 700 : 500,
            fontSize: '13px',
            fontFamily: 'Poppins, sans-serif',
            color: isDark ? '#f8fafc' : '#0f172a',
          }}>
            {info.getValue() as string || ''}
          </Box>
        );
      }
    },
    {
      header: "Sheet Title",
      accessorKey: "sheetTitle",
      cell: (info) => {
        const item = info.row.original as SheetIndexItem & { status?: string };
        const rawStatus = (item.status || item.notes || '').trim().toLowerCase();
        const isUpdated = rawStatus === 'updated' || Boolean(item.isUpdated);
        return (
          <Box sx={{
            fontWeight: isUpdated ? 700 : 400,
            fontSize: '13px',
            fontFamily: 'Poppins, sans-serif',
            color: isDark ? '#e2e8f0' : '#334155',
          }}>
            {info.getValue() as string || ''}
          </Box>
        );
      }
    },
    {
      header: "Discipline",
      accessorKey: "discipline",
      cell: (info) => {
        const raw = (info.getValue() as string || '').trim();
        const displayValue = normalizeTradeName(raw) || raw || '';
        return (
          <Box sx={{
            fontSize: '13px',
            fontFamily: 'Poppins, sans-serif',
            color: isDark ? '#94a3b8' : '#64748b',
          }}>
            {displayValue}
          </Box>
        );
      }
    },
    {
      header: "Status",
      id: "status",
      cell: (info) => {
        const item = info.row.original as SheetIndexItem & { status?: string };
        const rawStatus = (item.status || '').trim();
        const isUpdated = rawStatus.toLowerCase() === 'updated' || Boolean(item.isUpdated);
        const displayText = isUpdated ? 'Updated' : 'Unchanged';

        return (
          <Badge sx={{
            bg: isUpdated ? (isDark ? 'rgba(22, 163, 74, 0.2)' : '#DCFCE7') : (isDark ? 'rgba(148, 163, 184, 0.15)' : '#F1F5F9'),
            color: isUpdated ? (isDark ? '#4ade80' : '#15803D') : (isDark ? '#cbd5e1' : '#64748B'),
            border: `1px solid ${isUpdated
              ? (isDark ? '#166534' : '#86EFAC')
              : (isDark ? '#475569' : '#E2E8F0')}`,
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            px: '8px',
            py: '2px',
            textTransform: 'capitalize',
          }}>
            {displayText}
          </Badge>
        );
      }
    }
  ], [isDark]);

  const closeButton = (
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      style={{
        color: titleColor,
        background: 'transparent',
        border: 'none',
        fontSize: 22,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden="true">&times;</span>
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      centered
      size="xl"
      toggle={onClose}
      backdrop="static"
      keyboard={false}
      className="sheet-index-modal"
      style={{ maxWidth: '1050px', width: '85%' }}
      contentClassName="d-flex flex-column"
    >
      <ModalHeader close={closeButton} toggle={onClose} style={{ backgroundColor: headerBg, borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
        <h5 style={{ color: titleColor, fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '14px', margin: 0 }}>
          SHEET INDEX
          {stageName && (
            <span style={{ color: (stageColor && stageColor !== '#000000' && stageColor !== '#000') ? stageColor : accentColor }}>
              {' '}— {formatModalHeading(stageName).toUpperCase()}
            </span>
          )}
        </h5>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: modalBg,
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Banner Notice */}
        <Box sx={{
          bg: isDark ? 'rgba(2, 132, 199, 0.18)' : '#e0f2fe',
          color: isDark ? '#38bdf8' : '#0369a1',
          border: `1px solid ${isDark ? 'rgba(2, 132, 199, 0.4)' : '#bae6fd'}`,
          borderRadius: '6px',
          px: 3,
          py: '8px',
          mb: 3,
          fontSize: '12px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          fontFamily: 'Poppins, sans-serif',
          flexShrink: 0,
        }}>
          <FaInfoCircle size={14} style={{ flexShrink: 0 }} />
          <Box as="span"><strong>(Bold sheets are the updated sheets in this combined delivery)</strong></Box>
        </Box>

        {/* Trade Tabs */}
        {tradeTabs.length > 1 && (
          <Box sx={{
            display: 'flex',
            gap: '6px',
            mb: 3,
            borderBottom: `1px solid ${borderColor}`,
            pb: '8px',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            {tradeTabs.map(tab => {
              const isActive = activeTab === tab;
              const tabStyle = getTabStyle(tab, isActive, isDark, accentColor, tradesList, masterTasks);
              return (
                <Box
                  as="button"
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  sx={{
                    px: '14px',
                    py: '5px',
                    borderRadius: '6px',
                    border: `1px solid ${tabStyle.border}`,
                    bg: tabStyle.bg,
                    color: tabStyle.color,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontWeight: isActive ? 600 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      opacity: 0.9,
                    }
                  }}
                >
                  {tab}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Table */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', '& thead th': { position: 'sticky', top: 0, zIndex: 1 } }}>
          {filteredData.length === 0 ? (
            <Box sx={{
              textAlign: 'center',
              py: 5,
              color: isDark ? '#94a3b8' : '#64748b',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif'
            }}>
              No sheet index available for this delivery.
            </Box>
          ) : (
            <DataTable
              data={filteredData}
              columns={columns}
              showNavigation={false}
              onPageChange={() => { }}
              onPerPageChange={() => { }}
              perPageCount={filteredData.length || 10}
              TotalLength={filteredData.length}
              enableColumnFilters={false}
              noDataCentered={true}
              inlineLoader={false}
            />
          )}
        </Box>
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: headerBg,
          borderTop: `1px solid ${borderColor}`,
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <Button
          variant="buttons.CancelButton"
          onClick={onClose}
        >
          {closeButtonText || 'Close'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SheetIndexModal;
